import { useState } from 'react'
import { ExternalLink, Wallet, X } from 'lucide-react'
import { Pill } from '@/components/ui/pill'
import { formatDt } from '@/features/dashboard/format'
import { useDriverBilling } from '@/features/billing/use-recovery'
import {
  INVOICE_STATUS_META,
  PAYMENT_METHOD_LABELS,
  type RecoveryDriverIdentity,
} from '@/features/billing/recovery.types'
import RecordPaymentDialog from './RecordPaymentDialog'

interface RecoveryDriverDrawerProps {
  driver: RecoveryDriverIdentity
  onClose: () => void
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-TN')
}

/**
 * Fiche recouvrement d'un chauffeur (SCRUM-96) : dette, historique des factures et
 * des paiements, et bouton « Enregistrer un paiement » (dialog SCRUM-95).
 */
export default function RecoveryDriverDrawer({ driver, onClose }: RecoveryDriverDrawerProps) {
  const [payOpen, setPayOpen] = useState(false)
  const { data, isLoading, isError } = useDriverBilling(driver.id)
  const name = `${driver.firstName} ${driver.lastName}`.trim() || '—'

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/45" onClick={onClose}>
      <aside
        className="h-full w-[520px] max-w-[94vw] bg-bg shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-start gap-3 px-6 py-5 border-b border-line">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-xl text-ink tracking-tight truncate">
              {name}
            </div>
            <div className="font-mono text-[12px] text-muted mt-0.5">{driver.phone ?? '—'}</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" aria-label="Fermer">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-5">
          {isLoading && <p className="text-muted">Chargement…</p>}
          {isError && <p className="text-danger">Erreur lors du chargement de la fiche.</p>}

          {data && (
            <>
              {/* Solde */}
              <div className="flex items-center justify-between rounded-card bg-surface border border-line px-5 py-4">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-muted">
                    Dette courante
                  </div>
                  <div className="font-display font-bold text-2xl text-ink mt-1">
                    {formatDt(data.balanceDue)}
                  </div>
                </div>
                <Pill tone={data.status === 'SUSPENDED' ? 'dark' : 'success'} mono>
                  {data.status}
                </Pill>
              </div>

              <button
                onClick={() => setPayOpen(true)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-ink text-surface rounded-button h-12 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <Wallet size={18} strokeWidth={2.2} />
                Enregistrer un paiement
              </button>

              {/* Factures */}
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-muted mt-7 mb-2.5">
                Factures ({data.invoices.length})
              </h3>
              {data.invoices.length === 0 ? (
                <p className="text-[13px] text-muted">Aucune facture.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {data.invoices.map((inv) => {
                    const meta = INVOICE_STATUS_META[inv.status]
                    return (
                      <div
                        key={inv.id}
                        className="flex items-center gap-3 rounded-[14px] bg-surface border border-line px-4 py-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-ink">
                            {formatDt(inv.totalAmount)}
                            <span className="text-muted font-normal">
                              {' '}
                              · réglé {formatDt(inv.paidAmount)}
                            </span>
                          </div>
                          <div className="text-[11.5px] text-muted mt-0.5">
                            Échéance {fmtDate(inv.dueDate)}
                          </div>
                        </div>
                        <Pill tone={meta.tone} className="!px-2.5 !py-1 text-[11px]">
                          {meta.label}
                        </Pill>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Paiements */}
              <h3 className="text-[11px] font-bold uppercase tracking-wide text-muted mt-7 mb-2.5">
                Paiements ({data.payments.length})
              </h3>
              {data.payments.length === 0 ? (
                <p className="text-[13px] text-muted">Aucun paiement enregistré.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {data.payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 rounded-[14px] bg-surface border border-line px-4 py-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-ink">
                          {formatDt(p.amount)}
                          <span className="text-muted font-normal">
                            {' '}
                            · {PAYMENT_METHOD_LABELS[p.method]}
                          </span>
                        </div>
                        <div className="text-[11.5px] text-muted mt-0.5">
                          {fmtDate(p.createdAt)}
                          {p.reference ? ` · réf. ${p.reference}` : ''}
                        </div>
                      </div>
                      {p.proofUrl && (
                        <a
                          href={p.proofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-soft hover:text-ink"
                        >
                          Justificatif
                          <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {payOpen && (
        <RecordPaymentDialog
          driverId={driver.id}
          driverName={name}
          onClose={() => setPayOpen(false)}
          onRecorded={() => setPayOpen(false)}
        />
      )}
    </div>
  )
}
